"use client";

/**
 * Shared shell for the DS hover-animated control icons.
 *
 * Unlike the mount-animated icons in ./shared (which draw on once when they
 * appear), these sit idle and animate on hover — of the icon itself or of
 * any link/button wrapping it, so a whole toolbar button is the hover
 * target rather than the 16px glyph inside it. Inner elements opt in via
 * the `a-*` classes in ./hoverIcons.css.
 *
 * Colour comes from `currentColor`, and unlike ./shared there's no brand
 * default — these are chrome, so they inherit whatever the control sets:
 *
 *   import { AttachIcon } from "@/components/ui/design-system/icons";
 *
 *   <AttachIcon size={18} />                            // inherits
 *   <SendIcon size={16} className="text-white" />
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import "./hoverIcons.css";

export interface HoverIconProps {
  /** Pixel size of the (square) icon. Default 18. */
  size?: number;
  /** Extra classes — typically a `text-*` color override. */
  className?: string;
}

export function HoverIconSvg({
  size = 18,
  className,
  children,
}: HoverIconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size }}
      className={cn("nf-ds-ico block shrink-0", className)}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
